// Staff Completion Pass: after the client's signing sitting, staff completes and
// PIN-signs every remaining document as ONE river. PIN entered once at the start,
// presented per-document to the sign endpoint (each signature individually
// verified and recorded - the sitting holds the credential, not the ceremony).
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { resolveTokens, FormField } from '../EMRNotes';

const API = 'https://tcio-api.onrender.com';

export default function StaffCompletionPass({ patientId, episodeId, packetKeys, facilityName, rates, identity, onClose }) {
  const [queue, setQueue] = useState(null);
  const [idx, setIdx] = useState(0);
  const [pin, setPin] = useState('');
  const [pinSet, setPinSet] = useState(false);
  const [note, setNote] = useState(null);      // {id, content, formDef, sigs}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [gateFails, setGateFails] = useState([]);
  const [flash, setFlash] = useState(false);
  const [doneCount, setDoneCount] = useState(0);
  // Set aside, never skip (Chad 11/2): a document can wait, but the pass does not finish while any are waiting.
  const setAside = () => { setQueue(q => ({ docs: q.docs.map((d, i) => i === idx ? { ...d, skipped: true } : d) })); setIdx(i => i + 1); };
  const backToLeftovers = () => { setQueue(q => ({ docs: q.docs.filter(d => d.skipped).map(d => ({ ...d, skipped: false })) })); setIdx(0); };

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/api/emr/patients/${patientId}/client-pass-queue`,
      { params: { packet_keys: packetKeys.join(','), episode_id: episodeId || '' } });
    const docs = r.data.docs.filter(d => d.client_signed && d.note_id);
    setQueue({ docs });
  }, [patientId, episodeId, packetKeys]);

  useEffect(() => { load().catch(() => setErr('Could not load the completion queue')); }, [load]);

  // Load the current doc's note: content + whether staff already signed
  useEffect(() => {
    if (!queue || idx >= queue.docs.length) { setNote(null); return; }
    (async () => {
      setNote(null); setErr(''); setGateFails([]);
      try {
        const d = (await axios.get(`${API}/api/emr/notes/${queue.docs[idx].note_id}`)).data;
        const staffSigned = (d.signatures || []).some(s => s.signature_role !== 'client');
        if (staffSigned || ['finalized','pending_cosign','amended'].includes(d.note.status)) {
          setDoneCount(c => c + 1);
          setIdx(i => i + 1); return; // already done - river flows past
        }
        const cur = d.versions.find(v => v.id === d.note.current_version_id) || d.versions[0];
        // Fill-panel fix (Timothy 37558df1): pin the to-complete field set at load time.
        // Live-filtering on !content[key] unmounted inputs on the FIRST keystroke.
        const curContent = (cur && cur.content) || {};
        const flds = queue.docs[idx].fields || [];
        const fillKeys = flds.filter(f => f.type !== 'display' && f.required && !curContent[f.key]).map(f => f.key);
        setNote({ id: d.note.id, status: d.note.status, content: curContent,
          sigs: d.signatures || [], fields: flds, fill_keys: fillKeys });
      } catch { setErr('Could not load this document'); }
    })();
  }, [queue, idx]);

  const setField = (k, v) => setNote(n => ({ ...n, content: { ...n.content, [k]: v }, dirty: true }));
  // A kicked-back rule names its field; put that field on screen so it can be fixed here instead of skipped.
  const showFailedFields = (fails) => setNote(n => n ? { ...n, fill_keys: [...new Set([...(n.fill_keys || []), ...fails.map(x => x.details && x.details.field).filter(Boolean)])] } : n);

  const submitAndSign = async () => {
    setErr(''); setGateFails([]); setBusy(true);
    try {
      // 1. save current content as a new version (only if staff edited fields here)
      if (note.dirty) await axios.post(`${API}/api/emr/notes/${note.id}/versions`,
        { content: note.content, source: 'clinician_edit' });
      // 2. submit to compliance gate (only if not already past it)
      if (['in_review','kicked_back','ai_drafted'].includes(note.status)) {
        const sub = await axios.post(`${API}/api/emr/notes/${note.id}/submit`);
        const failed = ((sub.data && sub.data.results) || []).filter(x => !x.passed && x.severity === 'blocking');
        if (failed.length) { setGateFails(failed); showFailedFields(failed); setBusy(false); return; }
      }
      // 3. sign with the sitting PIN
      await axios.post(`${API}/api/emr/notes/${note.id}/sign`,
        { method: 'pin', pin, attestation_text: 'Reviewed and signed via staff completion pass' });
      setDoneCount(c => c + 1);
      setFlash(true);
      setTimeout(() => { setFlash(false); setIdx(i => i + 1); }, 700);
    } catch (e2) {
      const d = e2.response && e2.response.data;
      if (d && d.pin_required) setErr(d.error + ' - re-enter your PIN below');
      else if (d && d.results) { const f2 = (d.results || []).filter(x => !x.passed && x.severity === 'blocking'); setGateFails(f2); showFailedFields(f2); }
      else setErr((d && d.error) || 'Failed on this document');
    }
    setBusy(false);
  };

  if (!queue) return <div style={S.overlay}><div style={{ color:'#fff', fontSize:15 }}>{err || 'Loading completion pass…'}</div></div>;

  if (!pinSet) return (
    <div style={S.overlay}>
      <div style={{ ...S.card, maxWidth: 420, textAlign:'center' }}>
        <div style={{ fontWeight:700, fontSize:17, marginBottom:6 }}>Staff Completion Pass</div>
        <div style={{ fontSize:13, color:'#475569', marginBottom:14 }}>
          {queue.docs.length} client-signed documents. Enter your signature PIN once - it signs each
          document as you review and advance. Each signature is individually recorded.</div>
        <input type="password" inputMode="numeric" maxLength={4} value={pin}
          onChange={e => setPin(e.target.value.replace(/\D/g,''))}
          style={{ fontSize:24, letterSpacing:12, textAlign:'center', width:140, padding:'10px 0',
            border:'1.5px solid #cbd5e1', borderRadius:10 }} placeholder="••••" />
        <div style={{ display:'flex', gap:10, justifyContent:'center', marginTop:16 }}>
          <button style={S.primary} disabled={pin.length !== 4} onClick={() => setPinSet(true)}>Start</button>
          <button style={S.ghost} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );

  const done = idx >= queue.docs.length;
  const leftovers = queue.docs.filter(d => d.skipped);
  if (done && leftovers.length) return (
    <div style={S.overlay}>
      <div style={{ ...S.card, maxWidth: 480 }}>
        <div style={{ fontWeight:700, fontSize:17, marginBottom:6 }}>{leftovers.length} document{leftovers.length === 1 ? '' : 's'} still need{leftovers.length === 1 ? 's' : ''} you</div>
        <div style={{ fontSize:13, color:'#475569', marginBottom:10 }}>The pass is not finished until every client-signed document is completed and signed. These were set aside:</div>
        <ul style={{ margin:'0 0 14px 18px', padding:0, fontSize:13 }}>{leftovers.map(d => <li key={d.note_id}>{d.title}</li>)}</ul>
        <div style={{ display:'flex', gap:10 }}>
          <button style={S.primary} onClick={backToLeftovers}>Finish them now</button>
          <button style={S.ghost} onClick={onClose}>Exit — they stay open on the chart</button>
        </div>
      </div>
    </div>
  );
  if (done) return (
    <div style={S.overlay}>
      <div style={{ ...S.card, maxWidth: 420, textAlign:'center' }}>
        <div style={{ fontSize:34 }}>✓</div>
        <div style={{ fontWeight:700, fontSize:17, margin:'6px 0' }}>Completion pass finished</div>
        <div style={{ fontSize:13, color:'#475569', marginBottom:14 }}>{doneCount} documents completed and signed this sitting.</div>
        <button style={S.primary} onClick={onClose}>Close</button>
      </div>
    </div>
  );

  const doc = queue.docs[idx];
  const clientSig = note && note.sigs.filter(s => s.signature_role === 'client');
  const unanswered = note ? (note.fields || []).filter(f =>
    f.type !== 'display' && f.required &&
    (note.fill_keys ? note.fill_keys.includes(f.key) : !note.content[f.key])) : [];

  return (
    <div style={S.overlay}>
      <div style={S.card}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <div style={{ fontWeight:700, fontSize:15 }}>{doc.title}</div>
          <div style={{ fontSize:12, color:'#64748b' }}>{idx + 1} of {queue.docs.length}</div>
        </div>
        <div style={{ height:5, background:'#e2e8f0', borderRadius:3, marginBottom:12 }}>
          <div style={{ height:5, width:`${(idx / queue.docs.length) * 100}%`, background:'#4479B0', borderRadius:3 }} />
        </div>
        {!note && !err && <div style={{ padding:30, textAlign:'center', color:'#64748b' }}>Loading…</div>}
        {note && <>
          <div style={{ maxHeight:'34vh', overflowY:'auto', background:'#f8fafc', borderRadius:10, padding:'12px 14px', marginBottom:10 }}>
            {(doc.fields || []).filter(f => f.type === 'display' && (f.text || f.prompt)).map((f, i) => (
              <div key={i} style={{ fontSize:12.5, lineHeight:1.5, whiteSpace:'pre-wrap', marginBottom:8 }}>
                {resolveTokens(f.text || f.prompt, facilityName, rates, identity)}</div>
            ))}
          </div>
          {clientSig && clientSig.map((s, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 12px',
              background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, marginBottom:10 }}>
              {s.image_url && <img src={s.image_url} alt="client signature" style={{ maxWidth:150, maxHeight:52 }} />}
              <div style={{ fontSize:11.5, color:'#166534' }}>
                <div style={{ fontWeight:700 }}>{s.signer_name}</div>
                <div>{new Date(s.signed_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
          {unanswered.length > 0 && <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#92400e', marginBottom:6 }}>Complete before signing:</div>
            {unanswered.map(f => <FormField key={f.key} f={f} content={note.content} locked={false}
              setField={setField} facilityName={facilityName} rates={rates} identity={identity} />)}
          </div>}
          {gateFails.length > 0 && <div style={{ padding:'8px 12px', background:'#fef2f2', borderRadius:8, marginBottom:10 }}>
            {gateFails.map((g, i) => <div key={i} style={{ fontSize:12, color:'#b91c1c', fontWeight:600 }}>✗ {g.rule_description || g.description || g.code}</div>)}
          </div>}
          {err && <div style={{ color:'#dc2626', fontSize:12.5, fontWeight:600, marginBottom:10 }}>{err}
            {/pin/i.test(err) && <input type="password" inputMode="numeric" maxLength={4} value={pin}
              onChange={e => setPin(e.target.value.replace(/\D/g,''))}
              style={{ marginLeft:10, width:70, textAlign:'center', border:'1px solid #cbd5e1', borderRadius:6, padding:'4px 0' }} />}
          </div>}
          {flash && <div style={{ padding:'10px 14px', background:'#f0fdf4', border:'2px solid #22c55e',
            borderRadius:8, fontWeight:700, color:'#15803d', marginBottom:10 }}>✓ Signed</div>}
          <div style={{ display:'flex', gap:10 }}>
            <button style={S.primary} disabled={busy || flash} onClick={submitAndSign}>
              {busy ? 'Working…' : 'Submit & Sign → Next'}</button>
            <button style={S.ghost} disabled={busy} onClick={setAside}>Set aside for now</button>
            <button style={{ ...S.ghost, marginLeft:'auto' }} onClick={onClose}>Exit ({queue.docs.filter((d, i) => i >= idx || d.skipped).length} not signed yet)</button>
          </div>
        </>}
        {err && !note && <div style={{ color:'#dc2626', fontSize:13, fontWeight:600 }}>{err}
          <button style={{ ...S.ghost, marginLeft:10 }} onClick={setAside}>Set aside for now</button></div>}
      </div>
    </div>
  );
}

const S = {
  overlay: { position:'fixed', inset:0, background:'rgba(15,23,42,0.72)', zIndex:1000,
    display:'flex', alignItems:'center', justifyContent:'center', padding:16 },
  card: { background:'#fff', borderRadius:14, padding:'18px 20px', width:'100%', maxWidth:640,
    maxHeight:'92vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' },
  primary: { background:'#4479B0', color:'#fff', border:'none', borderRadius:9, padding:'10px 18px',
    fontWeight:700, fontSize:13.5, cursor:'pointer' },
  ghost: { background:'#fff', color:'#475569', border:'1.5px solid #cbd5e1', borderRadius:9,
    padding:'10px 14px', fontWeight:600, fontSize:13, cursor:'pointer' },
};
